// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack API Hooks — Projects, Phases, WorkItems
// apps/web/src/lib/api/projects.ts
//
// Uses @forgetrack/api-client for authenticated requests.
// All hooks follow the pattern: fetch on mount, expose refetch + mutate.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '@forgetrack/api-client';
import type {
  Project,
  Phase,
  WorkItem,
  CreateProjectRequest,
  UpdateProjectRequest,
  CreatePhaseRequest,
  CreateWorkItemRequest,
  ApiError,
} from './types';

// ─── Generic fetch hook ─────────────────────────────────────────────────────

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
}

function useApiFetch<T>(path: string, enabled = true): UseApiState<T> {
  const { createApi } = useApiClient();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const api = await createApi();
      const response = await api.get<T>(path);
      setData(response.data);
    } catch (err: any) {
      setError({
        message: err.response?.data?.message || err.message || 'Request failed',
        status: err.response?.status || 500,
        errors: err.response?.data?.errors,
      });
    } finally {
      setLoading(false);
    }
  }, [createApi, path]);

  useEffect(() => {
    if (enabled) fetch();
  }, [fetch, enabled]);

  return { data, loading, error, refetch: fetch };
}

// ─── Projects ───────────────────────────────────────────────────────────────

export function useProjects(enabled = true) {
  return useApiFetch<Project[]>('/api/v1/projects', enabled);
}

export function useProject(id: number) {
  return useApiFetch<Project>(`/api/v1/projects/${id}`, id > 0);
}

export function useProjectMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(
    async (data: CreateProjectRequest): Promise<Project | null> => {
      try {
        setLoading(true);
        setError(null);
        const api = await createApi();
        const response = await api.post<Project>('/api/v1/projects', data);
        return response.data;
      } catch (err: any) {
        setError({
          message: err.response?.data?.message || 'Failed to create project',
          status: err.response?.status || 500,
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  const update = useCallback(
    async (data: UpdateProjectRequest): Promise<Project | null> => {
      try {
        setLoading(true);
        setError(null);
        const api = await createApi();
        const response = await api.put<Project>(`/api/v1/projects/${data.id}`, data);
        return response.data;
      } catch (err: any) {
        setError({
          message: err.response?.data?.message || 'Failed to update project',
          status: err.response?.status || 500,
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  const archive = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        setLoading(true);
        setError(null);
        const api = await createApi();
        await api.post(`/api/v1/projects/${id}/archive`);
        return true;
      } catch (err: any) {
        setError({
          message: err.response?.data?.message || 'Failed to archive project',
          status: err.response?.status || 500,
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  const remove = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        setLoading(true);
        setError(null);
        const api = await createApi();
        await api.delete(`/api/v1/projects/${id}`);
        return true;
      } catch (err: any) {
        setError({
          message: err.response?.data?.message || 'Failed to delete project',
          status: err.response?.status || 500,
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { create, update, archive, remove, loading, error };
}

// ─── Complexity defaults (#3) ───────────────────────────────────────────────
//
// Project-level fallback values for the six WorkItem dimensional scores.
// New work items hydrate any NULL dimension from these defaults so the
// investor radar populates immediately. The classifier-derived path (#2)
// later upgrades ProjectDefault fields with per-conversation evidence.

export interface ComplexityDefaults {
  defaultArchitecturalDepth: number | null;
  defaultIntegrationPoints: number | null;
  defaultDomainExpertiseRequired: number | null;
  defaultInfrastructureExpertiseRequired: number | null;
  defaultSecurityExpertiseRequired: number | null;
  defaultNoveltyScore: number | null;
  defaultExpertiseDomainsJson: string | null;
}

export interface ApplyDefaultsResult {
  dryRun: boolean;
  scanned: number;
  workItemsTouched: number;
  totalFieldsFilled: number;
  /** Items whose composite ComplexityScore was (re)computed — the endpoint
   *  now rolls up composites even when no fields fill, so re-runs repair
   *  NULL composites (ruling W-1). */
  compositesUpdated: number;
}

export function useComplexityDefaults(projectId: number) {
  return useApiFetch<ComplexityDefaults>(
    `/api/v1/projects/${projectId}/complexity-defaults`,
    projectId > 0
  );
}

export function useComplexityDefaultsMutations(projectId: number) {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const save = useCallback(async (data: ComplexityDefaults): Promise<boolean> => {
    try {
      setLoading(true); setError(null);
      const api = await createApi();
      await api.put(`/api/v1/projects/${projectId}/complexity-defaults`, data);
      return true;
    } catch (err: any) {
      setError({
        message: err.response?.data?.message || 'Failed to save complexity defaults',
        status: err.response?.status || 500,
      });
      return false;
    } finally { setLoading(false); }
  }, [createApi, projectId]);

  const applyToOrphans = useCallback(
    async (dryRun: boolean): Promise<ApplyDefaultsResult | null> => {
      try {
        setLoading(true); setError(null);
        const api = await createApi();
        const res = await api.post<ApplyDefaultsResult>(
          `/api/v1/projects/${projectId}/apply-defaults-to-orphans?dryRun=${dryRun}`
        );
        return res.data;
      } catch (err: any) {
        setError({
          message: err.response?.data?.message || 'Apply-defaults failed',
          status: err.response?.status || 500,
        });
        return null;
      } finally { setLoading(false); }
    },
    [createApi, projectId]
  );

  return { save, applyToOrphans, loading, error };
}

// ─── Phases ─────────────────────────────────────────────────────────────────

export function usePhases(projectId: number) {
  return useApiFetch<Phase[]>(`/api/v1/phases?projectId=${projectId}`, projectId > 0);
}

export function usePhaseMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const create = useCallback(
    async (data: CreatePhaseRequest): Promise<Phase | null> => {
      try {
        setLoading(true);
        const api = await createApi();
        const response = await api.post<Phase>('/api/v1/phases', data);
        return response.data;
      } catch {
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  const update = useCallback(
    async (id: number, data: Partial<CreatePhaseRequest>): Promise<Phase | null> => {
      try {
        setLoading(true);
        const api = await createApi();
        const response = await api.put<Phase>(`/api/v1/phases/${id}`, data);
        return response.data;
      } catch {
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { create, update, loading };
}

// ─── Work Items ─────────────────────────────────────────────────────────────

export function useWorkItems(projectId: number, phaseId?: number) {
  const path = phaseId
    ? `/api/v1/work-items?projectId=${projectId}&phaseId=${phaseId}`
    : `/api/v1/work-items?projectId=${projectId}`;
  return useApiFetch<WorkItem[]>(path, projectId > 0);
}

export function useWorkItemMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const create = useCallback(
    async (data: CreateWorkItemRequest): Promise<WorkItem | null> => {
      try {
        setLoading(true);
        const api = await createApi();
        const response = await api.post<WorkItem>('/api/v1/work-items', data);
        return response.data;
      } catch {
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  const update = useCallback(
    async (id: number, data: Partial<CreateWorkItemRequest>): Promise<WorkItem | null> => {
      try {
        setLoading(true);
        const api = await createApi();
        const response = await api.put<WorkItem>(`/api/v1/work-items/${id}`, data);
        return response.data;
      } catch {
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  return { create, update, loading };
}
